import { useMutation } from '@tanstack/react-query'
import { createProcurementWorkflowSteps } from 'api/procurement.api'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { Input } from 'components/Form'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { AsyncMultiselect } from 'components/Select/AsyncMultiselect'
import { Option, Select } from 'components/Select/Select'
import { WORKFLOW_RESOLVER_TYPE_META, WORKFLOW_STEP_TYPE_META } from 'constants/meta'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Flex } from 'styling/GlobalStyles'
import { BigText } from 'styling/TypographyV2'
import { LocalWorkflowOptionStep, WorkflowResolverType, WorkflowStepType } from 'types/procurement'
import { baseUrl } from 'utils/axios'

export const WorkflowStepModal = ({
  closeModal,
  dataFunction,
  step,
}: {
  closeModal: () => void
  dataFunction: (data: LocalWorkflowOptionStep) => void
  step?: LocalWorkflowOptionStep
}) => {
  const [error, setError] = useState<boolean>(false)
  const [state, setState] = useState<LocalWorkflowOptionStep>({
    name: step?.name || '',
    type: step?.type || null,
    roles: step?.roles || null,
    resolverType: {
      label:
        step?.resolverType?.label || WORKFLOW_RESOLVER_TYPE_META[WorkflowResolverType.Roles]?.label,
      value: step?.resolverType?.value || WorkflowResolverType.Roles,
    },
    users: step?.users || null,
    canEditPR: step?.canEditPR || false,
  })
  const functionMutation = async () => {
    let data: LocalWorkflowOptionStep | undefined = undefined
    try {
      if (!state.type) {
        throw new Error('Step type is required')
      }
      if (!state.name) {
        throw new Error('Step name is required')
      }
      if (!state.resolverType) {
        throw new Error('Resolver type is required')
      }
      if (state.resolverType?.value === WorkflowResolverType.Roles) {
        if (!state.roles) {
          throw new Error('Roles are required')
        }
        data = {
          type: state.type,
          name: state.name,
          canEditPR: state.canEditPR,
          resolverType: state.resolverType,
          roles: state.roles,
          users: undefined,
        }
      }
      if (state.resolverType?.value === WorkflowResolverType.Users) {
        if (!state.users) {
          throw new Error('Users are required')
        }
        data = {
          type: state.type,
          name: state.name,
          canEditPR: state.canEditPR,
          resolverType: state.resolverType,
          users: state.users,
        }
      }
    } catch (error) {
      setError(true)
      toast.error(error.message)
      return
    }

    if (data) {
      dataFunction(data)
      closeModal()
    }
    return Promise.resolve()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <Modal
      onClose={() => {
        closeModal()
      }}
    >
      <ModalContent maxWidth='500px'>
        <ModalHeader>
          <BigText>Workflow Step</BigText>
        </ModalHeader>
        <ModalBody>
          <Flex column gap='1rem'>
            <Input
              placeholder='Enter step name'
              label='Step Name'
              name='name'
              value={state.name}
              onChange={handleChange}
              error={error && !state.name ? 'Step name is required' : null}
            />
            <Select
              placeholder='Select type'
              label='Step Type'
              options={
                Object.values(WorkflowStepType)
                  .filter((priority) => !isNaN(Number(priority)))
                  .map((type) => ({
                    label: WORKFLOW_STEP_TYPE_META[type]?.label,
                    value: type,
                  })) as Option[]
              }
              onChange={(value) => {
                setState((prev) => ({ ...prev, type: value }))
              }}
              value={state.type}
              error={error && !state.type ? 'Step type is required' : null}
            />
            <Select
              placeholder='Select resolver type'
              label='Resolver Type'
              options={
                Object.values(WorkflowResolverType)
                  .filter((priority) => !isNaN(Number(priority)))
                  .map((type) => ({
                    label: WORKFLOW_RESOLVER_TYPE_META[type]?.label,
                    value: type,
                  })) as Option[]
              }
              onChange={(value) => {
                setState((prev) => ({ ...prev, resolverType: value, roles: null, users: null }))
              }}
              value={state.resolverType}
              error={error && !state.resolverType ? 'Resolver type is required' : null}
            />
            {state.resolverType?.value === WorkflowResolverType.Roles && (
              <AsyncMultiselect
                queryKey='async-select-options-roles'
                decoder={(item: { id: number; name: string }) => ({
                  label: item.name,
                  value: item.id,
                })}
                placeholder='Select Role'
                label='Approvers/Roles'
                loadOptions={async (search) => {
                  const data = await baseUrl.get('/roles', {
                    params: {
                      q: search,
                    },
                  })
                  return data.data.data.map((item) => ({
                    id: item.id,
                    name: item.name,
                  })) as { id: number; name: string }[]
                }}
                onChange={(value) => {
                  setState((prev) => ({ ...prev, roles: value }))
                }}
                values={state.roles}
                error={error && !state.roles?.length ? 'Roles are required' : null}
              />
            )}
            {state.resolverType?.value === WorkflowResolverType.Users && (
              <AsyncMultiselect
                queryKey='async-select-options-users'
                decoder={(item: { id: number; name: string }) => ({
                  label: item.name,
                  value: item.id,
                })}
                placeholder='Select User'
                label='Approvers/Users'
                loadOptions={async (search) => {
                  const data = await baseUrl.get('/users', {
                    params: {
                      q: search,
                    },
                  })
                  return data.data.data.map((item) => ({
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName,
                  })) as { id: number; name: string }[]
                }}
                onChange={(value) => {
                  setState((prev) => ({ ...prev, users: value }))
                }}
                values={state.users}
                error={error && !state.users?.length ? 'Users are required' : null}
              />
            )}
            <CheckBox
              state={state.canEditPR}
              square
              fit
              title='Can edit PR'
              onClick={() => {
                setState((prev) => ({ ...prev, canEditPR: !prev.canEditPR }))
              }}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            orange
            onClick={() => {
              functionMutation()
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
